import { logger } from "@/utils/logger";
import { supabase } from '@/integrations/supabase/client';
import { SessionMessage } from '@/utils/sessionManager';
import { getDealerID } from '@/utils/dealerDataUtils';

interface ExtractedFormContext {
  message: string;
  department: 'Sales' | 'Service' | 'Parts' | 'Finance' | 'General';
  vehicle: {
    year: string | null;
    make: string | null;
    model: string | null;
    trim: string | null;
    vin: string | null;
    stock_number: string | null;
    color: string | null;
    mileage: string | null;
    price: string | null;
    vdp_url: string | null;
  } | null;
  preferences: {
    budget: string | null;
    trade_in: string | null;
    preferred_contact_method: string | null;
    preferred_appointment_date: string | null;
    preferred_appointment_time: string | null;
  } | null;
}

interface ExtractionResult {
  success: boolean;
  data: ExtractedFormContext;
  usage?: {
    prompt_tokens: number;
    completion_tokens: number;
    duration_ms: number;
  };
}

/**
 * Extracts form context from conversation using OpenAI.
 * Called on form submission to get AI summary and vehicle/preference data.
 * Falls back gracefully if extraction fails.
 */
export async function extractFormContext(
  conversation: SessionMessage[],
  formType: string,
  formData?: { [key: string]: string }
): Promise<ExtractedFormContext> {
  logger.log('📝 [EXTRACT] Starting form context extraction...');
  
  // Default fallback context
  const fallbackContext: ExtractedFormContext = {
    message: 'Customer submitted a form through the chat assistant.',
    department: 'General',
    vehicle: null,
    preferences: null
  };
  
  // Skip extraction if conversation is too short
  if (!conversation || conversation.length < 2) {
    logger.log('📝 [EXTRACT] Conversation too short, using fallback');
    return fallbackContext;
  }
  
  try {
    const dealerId = await getDealerID();
    
    // Format conversation for the edge function
    const formattedConversation = conversation.map(msg => ({
      role: msg.sender === 'user' ? 'user' : 'assistant',
      content: msg.content
    }));
    
    logger.log(`📝 [EXTRACT] Calling edge function with ${formattedConversation.length} messages`);
    if (formData) {
      logger.log(`📝 [EXTRACT] Including form data with ${Object.keys(formData).length} fields`);
    }
    
    const { data, error } = await supabase.functions.invoke('extract-form-context', {
      body: {
        conversation: formattedConversation,
        dealerId: dealerId,
        formType: formType,
        formData: formData || null
      }
    });
    
    if (error) {
      console.error('📝 [EXTRACT] Edge function error:', error);
      return fallbackContext;
    }
    
    const result = data as ExtractionResult;
    
    if (result.success && result.data) {
      logger.log('📝 [EXTRACT] Successfully extracted context:', {
        department: result.data.department,
        hasVehicle: !!result.data.vehicle,
        hasPreferences: !!result.data.preferences,
        messageSummary: result.data.message?.substring(0, 50) + '...',
        usage: result.usage
      });
      return result.data;
    }
    
    // Edge function returned failure but with fallback data
    if (result.data) {
      logger.log('📝 [EXTRACT] Extraction failed, using edge function fallback');
      return result.data;
    }
    
    return fallbackContext;
    
  } catch (error) {
    console.error('📝 [EXTRACT] Extraction failed:', error);
    return fallbackContext;
  }
}

/**
 * Merges extracted context with existing interest data.
 * Prioritizes user-clicked vehicle data over AI-extracted data.
 */
export function mergeWithExistingInterest(
  extractedContext: ExtractedFormContext,
  existingInterestData: any
): any {
  logger.log('📝 [MERGE] Merging extracted context with existing interest data');
  logger.log('📝 [MERGE] Existing interest data:', JSON.stringify(existingInterestData, null, 2));
  
  // Start with existing interest data or empty object
  const merged = existingInterestData ? { ...existingInterestData } : {};
  
  // Always use AI-generated message (summary)
  merged.message = extractedContext.message;
  
  // Map department to expected format
  // The existing code expects 'New', 'Used', 'Service', 'Finance' 
  if (extractedContext.department) {
    // Service-related intents (Service, Parts) ALWAYS map to 'Service' and cannot be overridden
    if (extractedContext.department === 'Service' || extractedContext.department === 'Parts') {
      merged.department = 'Service';
      logger.log(`📝 [MERGE] Service intent detected from AI (${extractedContext.department}) - setting department to Service (non-overridable)`);
    } else if (extractedContext.department === 'Sales' || extractedContext.department === 'General') {
      // Keep existing department if set, otherwise don't set (will be determined by vehicle data)
      if (!merged.department) {
        merged.department = 'New'; // Default for sales
      }
    } else {
      merged.department = extractedContext.department;
    }
  }
  
  // Merge vehicle data - ONLY if no existing vehicle data from user clicks
  if (extractedContext.vehicle && !merged.vehicle) {
    logger.log('📝 [MERGE] Adding AI-extracted vehicle data (no user-clicked vehicle)');
    merged.vehicle = {
      year: extractedContext.vehicle.year,
      make: extractedContext.vehicle.make,
      model: extractedContext.vehicle.model,
      trim: extractedContext.vehicle.trim,
      vin: extractedContext.vehicle.vin,
      stock: extractedContext.vehicle.stock_number,
      color: extractedContext.vehicle.color,
      mileage: extractedContext.vehicle.mileage,
      price: extractedContext.vehicle.price,
      vdp_url: extractedContext.vehicle.vdp_url
    };
  } else if (merged.vehicle) {
    logger.log('📝 [MERGE] Keeping existing user-clicked vehicle data');
  }
  
  // Merge preferences - fill in missing fields only
  if (extractedContext.preferences) {
    if (!merged.budget && extractedContext.preferences.budget) {
      merged.budget = extractedContext.preferences.budget;
    }
    if (!merged.trade_in && extractedContext.preferences.trade_in) {
      merged.trade_in = extractedContext.preferences.trade_in;
    }
    if (!merged.preferred_contact_method && extractedContext.preferences.preferred_contact_method) {
      merged.preferred_contact_method = extractedContext.preferences.preferred_contact_method;
    }
    if (!merged.appointmentDate && extractedContext.preferences.preferred_appointment_date) {
      merged.appointmentDate = extractedContext.preferences.preferred_appointment_date;
    }
    if (!merged.appointmentTime && extractedContext.preferences.preferred_appointment_time) {
      merged.appointmentTime = extractedContext.preferences.preferred_appointment_time;
    }
  }
  
  logger.log('📝 [MERGE] Final merged interest data:', JSON.stringify(merged, null, 2));
  return merged;
}

